import type { AtendimentoDoDia } from '@mymaxx/shared';
import { Botao, Modal } from '@mymaxx/ui';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Star } from 'lucide-react';
import { useState } from 'react';
import { atendimentoApi } from '../lib/atendimento-api';
import { FioDeLeitura } from './Bolha';

/**
 * A conferência de fim de turno do atendente: tudo que passou pelas mãos dele hoje, com
 * o motivo do encerramento e a nota do cliente.
 *
 * Existe porque o relatório completo é de supervisor e admin; o atendente não vê nem os
 * próprios números. Aqui ele vê só os dele, que é o que precisa para conferir o dia.
 */
export function MeuDia({ onFechar }: { onFechar: () => void }) {
  const dia = useQuery({ queryKey: ['meu-dia'], queryFn: atendimentoApi.meuDia });
  const linhas = dia.data ?? [];
  const avaliados = linhas.filter((l) => l.avaliacao !== null);
  const media = avaliados.length
    ? Math.round((avaliados.reduce((s, l) => s + (l.avaliacao ?? 0), 0) / avaliados.length) * 10) /
      10
    : null;
  /**
   * O atendimento aberto para releitura.
   *
   * Troca o conteúdo desta mesma janela em vez de abrir outra por cima: o atendente veio de
   * uma lista e volta para ela: duas janelas empilhadas só dariam dois "fechar" para errar.
   */
  const [aberto, setAberto] = useState<AtendimentoDoDia | null>(null);

  if (aberto) {
    return <Leitura atendimento={aberto} onVoltar={() => setAberto(null)} onFechar={onFechar} />;
  }

  return (
    <Modal aberto titulo="Meus atendimentos de hoje" onFechar={onFechar}>
      <div className="space-y-3">
        <div className="flex gap-6 text-sm">
          <Numero rotulo="Atendimentos" valor={String(linhas.length)} />
          <Numero rotulo="Avaliados" valor={String(avaliados.length)} />
          <Numero rotulo="Nota média" valor={media === null ? '—' : media.toFixed(1)} />
        </div>

        {dia.isLoading && <p className="text-sm text-neutral-500">Carregando…</p>}
        {dia.isSuccess && linhas.length === 0 && (
          <p className="text-sm text-neutral-500">Nenhum atendimento hoje ainda.</p>
        )}

        {linhas.length > 0 && (
          <p className="text-xs text-neutral-500">
            Clique num atendimento para reler a conversa inteira.
          </p>
        )}

        <ul className="max-h-[60vh] space-y-1 overflow-y-auto">
          {linhas.map((l) => (
            <Linha key={l.id} atendimento={l} onAbrir={() => setAberto(l)} />
          ))}
        </ul>
      </div>
    </Modal>
  );
}

/**
 * Um atendimento de hoje lido por inteiro, no formato do chat e com as mídias.
 *
 * Quem recebeu uma nota baixa quer saber o que aconteceu naquela conversa — e até agora o
 * atendente via só a linha com o protocolo e a estrela. Reler é a única forma de aprender
 * alguma coisa com a avaliação (pedido de 30/09).
 *
 * É leitura: a `Bolha` sem `onResponder` não mostra botão de responder nem tiquinhos.
 */
function Leitura({
  atendimento,
  onVoltar,
  onFechar,
}: {
  atendimento: AtendimentoDoDia;
  onVoltar: () => void;
  onFechar: () => void;
}) {
  const conversa = useQuery({
    queryKey: ['conversa', atendimento.id],
    queryFn: () => atendimentoApi.detalhe(atendimento.id),
  });

  return (
    <Modal
      aberto
      titulo={atendimento.cliente ?? 'Sem identificação'}
      descricao={`${atendimento.protocolo}${atendimento.setor ? ` · ${atendimento.setor}` : ''}`}
      onFechar={onFechar}
      largura="larga"
      alturaCheia
      acoes={
        <Botao variante="secundario" onClick={onVoltar}>
          <ArrowLeft size={15} /> Voltar à lista
        </Botao>
      }
    >
      <FioDeLeitura
        mensagens={conversa.data?.mensagens ?? []}
        carregando={conversa.isPending}
        vazio="Este atendimento não tem mensagens."
      />
    </Modal>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div>
      <p className="text-xl font-semibold">{valor}</p>
      <p className="text-xs text-neutral-500">{rotulo}</p>
    </div>
  );
}

function Linha({ atendimento, onAbrir }: { atendimento: AtendimentoDoDia; onAbrir: () => void }) {
  const aberto = atendimento.status !== 'encerrada';
  return (
    <li className="rounded-lg border border-neutral-200 text-sm transition hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-700 dark:hover:bg-neutral-800/50">
      <button type="button" onClick={onAbrir} className="w-full px-3 py-2 text-left">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-medium">{atendimento.cliente ?? 'Sem identificação'}</p>
            <p className="truncate font-mono text-[11px] text-neutral-500">
              {atendimento.protocolo}
            </p>
          </div>
          <div className="shrink-0 text-right">
            {aberto ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                Em aberto
              </span>
            ) : (
              <span className="text-[11px] text-neutral-500">
                {duracao(atendimento.duracaoSeg)}
              </span>
            )}
            {atendimento.avaliacao !== null && (
              <p className="mt-0.5 flex items-center justify-end gap-0.5 text-[11px]">
                <Star size={11} className="fill-amber-400 text-amber-400" />
                {atendimento.avaliacao}
              </p>
            )}
          </div>
        </div>
        {(atendimento.setor || atendimento.motivo) && (
          <p className="mt-1 text-[11px] text-neutral-500">
            {[atendimento.setor, atendimento.motivo].filter(Boolean).join(' · ')}
          </p>
        )}
        {atendimento.avaliacaoComentario && (
          <p className="mt-1 border-l-2 border-neutral-300 pl-2 text-[11px] text-neutral-600 dark:border-neutral-600 dark:text-neutral-300">
            “{atendimento.avaliacaoComentario}”
          </p>
        )}
      </button>
    </li>
  );
}

/** Duração em linguagem de gente: "38 min", "1 h 12 min". */
function duracao(segundos: number | null): string {
  if (segundos === null) return '';
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return `${minutos} min`;
  return `${Math.floor(minutos / 60)} h ${minutos % 60} min`;
}
